import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined, ProjectOutlined, TableOutlined } from '@ant-design/icons';
import { WorkspaceView, WorkspaceViewRepresentation } from '../../../types/WorkspaceView';
import { FlagDefault } from '../../../assets/CustomIcons/FlagDefault';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';

export const ViewsGroup: React.FC<{
  views: WorkspaceView[];
  selectedView: WorkspaceView;
  setSelectedView: (view: WorkspaceView) => void;
  updateViews: (views: WorkspaceView[]) => void;
  changedViews: number[];
}> = ({ views, selectedView, setSelectedView, updateViews, changedViews }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  function onDragStart(event: DragStartEvent) {}

  async function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const indexWhereDropeed = views.findIndex((v) => v.id === overId);
    const indexFrom = views.findIndex((v) => v.id === activeId);

    const orderArray = arrayMove(views, indexFrom, indexWhereDropeed);

    const newViews = orderArray.map((view, index) => {
      return {
        ...view,
        orderIndex: index,
      };
    });

    updateViews(newViews);
  }

  function onDragOver(event: DragOverEvent) {}

  const sortedViews = views.sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id));

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver}>
      <SortableContext items={views.map((view) => view.id)}>
        {sortedViews.map((view: WorkspaceView) => (
          <ViewSortable
            key={view.id}
            view={view}
            selected={view.id === selectedView.id}
            changed={changedViews.includes(view.id)}
            onClick={() => setSelectedView(view)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export const ViewSortable: React.FC<{
  key: number;
  view: WorkspaceView;
  selected: boolean;
  changed: boolean;
  onClick: () => void;
}> = ({ view, selected, changed, onClick }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: view.id,
    data: {
      type: 'ViewProperty',
      view,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style }}
      {...attributes}
      {...listeners}
      className={`flex gap-1.5 items-center px-1 py-1.5 cursor-pointer hover:bg-neutral-300 rounded my-1 outline ${selected ? 'outline-1 outline-taskonioGreen-200 bg-taskonioGreen-100 hover:bg-taskonioGreen-100' : 'outline-none hover:bg-neutral-300'}`}
      key={view.id}
      onClick={onClick}
    >
      <HolderOutlined className="mr-1" />
      {view.representation === WorkspaceViewRepresentation.table && (
        <TableOutlined className={selected ? 'text-brand-grey-dark' : 'text-neutral-900'} />
      )}
      {view.representation === WorkspaceViewRepresentation.kanban && (
        <ProjectOutlined className={selected ? 'text-brand-grey-dark' : 'text-neutral-900'} />
      )}
      <CustomTooltip title={view.name.length > 26 ? view.name : ''} placement="top">
        <div
          className={`${selected ? 'text-brand-grey-dark' : 'text-neutral-900'} text-sm font-normal leading-tight truncate`}
        >
          {view.name}
        </div>
      </CustomTooltip>
      {changed && <div className="w-2 h-2 bg-orange-600 rounded-full flex-shrink-0" />}

      {view.default && (
        <div className="flex flex-grow justify-end text-neutral-900">
          <FlagDefault />
        </div>
      )}
    </div>
  );
};
